<script setup lang="ts">
import { MapPin, BarChart3, BookOpen, ClipboardCheck, ChevronRight } from 'lucide-vue-next'
import type { PaletteHue } from '@/design-system/components'
import TodayWidget from './TodayWidget.vue'

/** Prepared attention row (built in Today.vue). */
export interface AttentionRow {
  id: string
  kind: 'grade' | 'plan' | 'register'
  hue: PaletteHue
  title: string
  className: string
  meta: string
  count?: number | null
  route: string
}

defineProps<{ rows: AttentionRow[] }>()
const emit = defineEmits<{ open: [route: string]; reviewAll: [] }>()

const KIND_ICON = { grade: BarChart3, plan: BookOpen, register: ClipboardCheck }
</script>

<template>
  <TodayWidget :icon="MapPin" icon-hue="amber" title="Needs attention" :count="rows.length">
    <p v-if="!rows.length" class="att__empty">Nothing needs you right now.</p>
    <div v-else class="att__list">
      <button
        v-for="(a, i) in rows"
        :key="a.id"
        type="button"
        class="att__row"
        :class="{ 'att__row--divided': i > 0 }"
        @click="emit('open', a.route)"
      >
        <span class="att__tile">
          <component :is="KIND_ICON[a.kind]" :size="15" />
        </span>
        <span class="att__text">
          <span class="att__title">{{ a.title }}</span>
          <span class="att__sub">
            <span class="att__dot" :style="{ background: a.hue.solid }" />
            {{ a.className }} · {{ a.meta }}
          </span>
        </span>
        <span v-if="a.count != null" class="att__count">{{ a.count }}</span>
        <ChevronRight :size="14" class="att__chev" />
      </button>
    </div>

    <template #footer>
      <button type="button" class="att__review" @click="emit('reviewAll')">
        Review all <ChevronRight :size="13" />
      </button>
    </template>
  </TodayWidget>
</template>

<style scoped>
.att__empty {
  margin: 4px 0 10px;
  font-size: 13px;
  color: var(--ink-400);
}
.att__list {
  display: flex;
  flex-direction: column;
}
.att__row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 11px 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font-family: var(--font-sans);
}
.att__row--divided {
  border-top: 1px solid var(--hairline);
}
.att__tile {
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border-radius: 9px;
  background: var(--ink-100);
  color: var(--ink-700);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.att__text {
  flex: 1;
  min-width: 0;
}
.att__title {
  display: block;
  font-size: 13.5px;
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.att__sub {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
  font-size: 12px;
  color: var(--ink-500);
}
.att__dot {
  width: 7px;
  height: 7px;
  border-radius: 2px;
  flex-shrink: 0;
}
.att__count {
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}
.att__chev {
  color: var(--ink-300);
  flex-shrink: 0;
}
.att__review {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-700);
}
</style>
